import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Heading,
  SelectField,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Get, api3Post } from '../api/api3Client';

const STATUS_FILTERS = [
  { value: 'needs_review', label: 'Needs decision' },
  { value: 'confirmed', label: 'Denial confirmed' },
  { value: 'all', label: 'All denied' },
];

function queueBadgeStyle(state) {
  if (state === 'awaiting_admin') {
    return { background: '#fde8e8', color: '#9b1c1c', padding: '2px 6px', borderRadius: '4px' };
  }
  if (state === 'confirmed') {
    return { background: '#e8f4fd', color: '#1c4a6b', padding: '2px 6px', borderRadius: '4px' };
  }
  return undefined;
}

function formatTimestamp(ts) {
  if (ts === undefined || ts === null || ts === '') return '—';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return String(ts);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatUnix(ts) {
  if (ts === undefined || ts === null || ts === '') return '—';
  const n = Number(ts);
  if (!Number.isFinite(n)) return String(ts);
  const ms = n > 1e12 ? n : n * 1000;
  return formatTimestamp(ms);
}

export default function AdminEndorsementReview() {
  const [statusFilter, setStatusFilter] = useState('needs_review');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  async function loadList(overrideFilter) {
    const filter = typeof overrideFilter === 'string' ? overrideFilter : statusFilter;
    setLoading(true);
    setError('');
    try {
      const res = await api3Get('/admin/portal/endorsement-review', {
        statusFilter: filter,
        q: query,
        limit: 100,
      });
      setItems(res.items || []);
    } catch (e) {
      setError(e.message || String(e));
      setItems([]);
    }
    setLoading(false);
  }

  async function loadDetail(endorsementId) {
    setLoading(true);
    setError('');
    setSelectedId(endorsementId);
    try {
      const res = await api3Get(`/admin/portal/endorsement-review/${endorsementId}`);
      setDetail(res.endorsement);
    } catch (e) {
      setError(e.message || String(e));
      setDetail(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadList();
  }, [statusFilter]);

  function requestAction(action) {
    if (!detail) return;
    const labels = {
      override_approved: 'Override to approved (tech issue)',
      confirm_denial: 'Confirm denial (real rejection — auto cancel/refund reservation)',
    };
    setConfirm({
      action,
      label: `${labels[action] || action} for driver ${detail.driverId}?`,
    });
  }

  async function executeConfirm() {
    if (!confirm || !detail) return;
    setActionLoading(true);
    setError('');
    setInfo('');
    try {
      const res = await api3Post(
        `/admin/portal/endorsement-review/${detail.endorsementId}/action`,
        { action: confirm.action, note: note || undefined }
      );
      setConfirm(null);
      setNote('');
      setInfo(res.message || `Action ${confirm.action} applied.`);
      if (res.result === 'approved' || res.result === 'denial_confirmed') {
        setDetail(null);
        setSelectedId(null);
      }
      await loadList();
    } catch (e) {
      setError(e.message || String(e));
    }
    setActionLoading(false);
  }

  return (
    <View padding="1rem">
      <Heading level={3}>Admin: Endorsement Review</Heading>
      <Text>
        Review accidental Pouch denials. Override to approved if it was a tech issue;
        confirm denial if real — reservation monitor will auto-cancel/refund (no suspension).
      </Text>

      <Flex direction="row" gap="0.5rem" alignItems="flex-end" wrap="wrap" marginTop="1rem">
        <SelectField
          label="Status filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button onClick={() => loadList()} isLoading={loading}>
          Refresh
        </Button>
      </Flex>

      {error ? (
        <Alert variation="error" isDismissible={false} marginTop="1rem">
          {error}
        </Alert>
      ) : null}

      {info ? (
        <Alert variation="info" isDismissible={false} marginTop="1rem">
          {info}
        </Alert>
      ) : null}

      {loading && !detail ? <Loadingmini /> : null}

      <Flex direction="row" gap="1rem" marginTop="1rem" wrap="wrap">
        <View flex="1" minWidth="300px">
          <Heading level={5}>Denied endorsements</Heading>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th align="left">Driver</th>
                <th align="left">Policy</th>
                <th align="left">Fleet</th>
                <th align="left">Queue</th>
                <th align="left">Denied at</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.endorsementId}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === row.endorsementId ? '#eef6ff' : undefined,
                  }}
                  onClick={() => loadDetail(row.endorsementId)}
                >
                  <td>
                    {row.displayName} ({row.driverId})
                  </td>
                  <td>{row.policyNumber || row.policyId}</td>
                  <td>{row.fleetCode || row.fleetId}</td>
                  <td>
                    {row.reviewQueueLabel ? (
                      <span style={queueBadgeStyle(row.reviewQueueState)}>
                        {row.reviewQueueLabel}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatTimestamp(row.statusTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </View>

        {detail ? (
          <View flex="1" minWidth="320px">
            <Heading level={5}>
              Driver {detail.driverId} — endorsement {detail.endorsementId}
            </Heading>
            <Text>Status: {detail.status}</Text>
            <Text>Policy: {detail.policyNumber}</Text>
            <Text>Request ID: {detail.requestId || '—'}</Text>
            <Text>Denied at: {formatTimestamp(detail.statusTime)}</Text>
            {detail.reviewQueueLabel ? (
              <Text marginTop="0.25rem">
                Queue:{' '}
                <span style={queueBadgeStyle(detail.reviewQueueState)}>
                  {detail.reviewQueueLabel}
                </span>
              </Text>
            ) : null}
            <Text marginTop="0.5rem">{detail.reviewNotes}</Text>

            <Heading level={6} marginTop="1rem">
              Open reservations
            </Heading>
            {detail.openReservations?.length ? (
              <ul>
                {detail.openReservations.map((r) => (
                  <li key={r.resId}>
                    {r.resId} — {r.status} — car {r.carId} — start {formatUnix(r.start)}
                  </li>
                ))}
              </ul>
            ) : (
              <Text>None</Text>
            )}

            <TextField
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              marginTop="1rem"
            />

            <Flex direction="row" gap="0.5rem" wrap="wrap" marginTop="1rem">
              {(detail.allowedActions || []).map((action) => (
                <Button key={action} onClick={() => requestAction(action)}>
                  {action === 'override_approved'
                    ? 'Override to approved'
                    : 'Confirm denial'}
                </Button>
              ))}
            </Flex>

            {confirm ? (
              <Alert variation="warning" marginTop="1rem">
                <Text>{confirm.label}</Text>
                <Flex direction="row" gap="0.5rem" marginTop="0.5rem">
                  <Button
                    variation="primary"
                    onClick={executeConfirm}
                    isLoading={actionLoading}
                  >
                    Confirm
                  </Button>
                  <Button onClick={() => setConfirm(null)} isDisabled={actionLoading}>
                    Cancel
                  </Button>
                </Flex>
              </Alert>
            ) : null}
          </View>
        ) : null}
      </Flex>
    </View>
  );
}
